import { usePermissao } from '@/utils/permissao';
import { Form, Formik, FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { EmpresaService } from '../../services';
import { Empresa } from '@/types/Empresa';
import { EntityIcon } from '@/components/ui/cores';
import { PageHeader } from '@/components/ui/page-header';
import { FormActions } from '@/components/ui/form-actions';
import { Row } from '@/components/grid/row';
import { Card } from '@/components/ui/card';
import { FormikInput } from '@/components/ui/input';
import { getTenant } from '@/utils/TenantUtils';
import { ErrorPage } from '../ErrorPage';
import { SectionTitle } from '@/components/ui/section-title';
import { MARCA_PADRAO, useMarca } from '@/components/providers/MarcaProvider';
import { ImagemInput } from './ImagemInput';
import { Palette } from 'lucide-react';
import { ReactComponent as Logo } from '@/assets/img/logo-transparent.svg';

const empresaFormValidationSchema = Yup.object().shape({
    nome: Yup.string().required('Informe o nome da corretora'),
    cnpj: Yup.string(),
});

const EmpresaFormPage = () => {
    const [empresa, setEmpresa] = useState<Empresa>({ nome: '', cnpj: '', ativo: true, usuarioAdministrador: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<AxiosError>();
    const marca = useMarca();
    const somenteLeitura = !usePermissao('EMPRESA_WRITE');

    useEffect(() => {
        EmpresaService.getById(getTenant()).then(({ data }) => setEmpresa(data)).catch(setError);
    }, []);

    const onSave = (values: Empresa) => {
        setLoading(true);
        EmpresaService.update(values, values.id!)
            .then(({ data }) => {
                setEmpresa(data);
                marca.recarregar();
                toast.success('Dados da corretora salvos.');
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }

    if (error) {
        return <ErrorPage axiosError={error} />
    }

    return (
        <div className="space-y-6">
            <PageHeader icon={<EntityIcon entidade="empresa" />} title="Dados da corretora" />
            <Card className='p-6 shadow-none'>
                <Formik initialValues={empresa} validationSchema={empresaFormValidationSchema} onSubmit={onSave} enableReinitialize>
                    {(formikProps: FormikProps<Empresa>) => (
                        <Form>
                            <fieldset disabled={somenteLeitura} className="min-w-0">
                            <Row>
                                <FormikInput name='nome' label='Nome' size={8} />
                                <FormikInput name='cnpj' label='CNPJ' size={4} />
                            </Row>
                            <SectionTitle icon={<Palette />} cor="bg-sky-500/10 text-sky-600 dark:text-sky-400"
                                description={`Como o sistema aparece para os usuários da corretora. O que ficar em branco usa o padrão ${MARCA_PADRAO}.`}>
                                Marca
                            </SectionTitle>
                            <Row>
                                <FormikInput name='nomeMenu' label='Nome no menu' placeholder={MARCA_PADRAO} size={6} />
                            </Row>
                            <div className="mt-4 grid gap-6 sm:grid-cols-2">
                                <ImagemInput id="logo" label="Logo do menu" descricao="PNG, SVG ou JPG de até 300 KB. Fundo transparente fica melhor."
                                    valor={formikProps.values.logo} onChange={v => formikProps.setFieldValue('logo', v)}
                                    padrao={<Logo className="h-8 w-8 fill-primary" />} />
                                <ImagemInput id="icone" label="Ícone do site" descricao="Aparece na aba do navegador. Quadrado, PNG ou ICO de até 300 KB."
                                    valor={formikProps.values.icone} onChange={v => formikProps.setFieldValue('icone', v)}
                                    padrao={<img src={`${process.env.PUBLIC_URL}/favicon.ico`} alt="" className="h-8 w-8" />} />
                            </div>
                            </fieldset>
                            <FormActions loading={loading} onSave={formikProps.submitForm} somenteLeitura={somenteLeitura} />
                        </Form>
                    )}
                </Formik>
            </Card>
        </div>
    );
}

export default EmpresaFormPage;
